import { Injectable } from "@angular/core";
import { from, of, timer } from "rxjs";
import { switchMap, catchError, map } from "rxjs/operators";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { AnalyticsService, METRIC } from "@apps/patient-core/src/app/services/analytics.service";
import { BaseAuthService } from "@apps/patient-core/src/app/services/abstracts/auth.service";
import { Constants } from "@apps/concierge-app/constants";
import { ConciergeService } from "./concierge.service";

dayjs.extend(duration);

@Injectable({ providedIn: "root" })
export class ConnectionStatusService {
  private _lastOfflineTime: number | null = null;

  constructor(private _analyticsService: AnalyticsService, private _authService: BaseAuthService, private _conciergeService: ConciergeService) {}

  public pollConnection() {
    timer(0, Constants.CONNECTION_MONITOR_INTERVAL)
      .pipe(
        switchMap(() => {
          // Use a ping endpoint to check if the app is online as window.navigator.onLine will return true if the user has a LAN connection but no internet access
          const url = "/assets/ping.png?cb=" + Date.now();
          //use fetch to prevent hitting the http interceptors
          return from(fetch(url, { method: "GET", cache: "no-cache" })).pipe(
            map((response) => response.ok),
            catchError(() => of(false))
          );
        })
      )
      .subscribe((nowOnline) => {
        const wasOnline = this._lastOfflineTime === null;

        if (wasOnline && !nowOnline) {
          // If we were online and now we're offline, record the time we went offline
          this._lastOfflineTime = Date.now();
        } else if (!wasOnline && nowOnline && this._lastOfflineTime) {
          // If we were offline and now we're online, handle the online event
          this._handleOnlineEvent();
        }
      });
  }

  private _formatOfflineDuration(start: number, end: number) {
    const diffMs = end - start;
    const diffDuration = dayjs.duration(diffMs);
    const days = diffDuration.days();
    const hours = diffDuration.hours();
    const minutes = diffDuration.minutes();
    const seconds = diffDuration.seconds();

    const formattedDiff = `${days > 0 ? `${days}d ` : ""}${hours > 0 ? `${hours}h ` : ""}${minutes > 0 ? `${minutes}m ` : ""}${seconds}s`;

    return formattedDiff;
  }

  private _handleOnlineEvent() {
    if (!this._lastOfflineTime) return;

    const offlineDuration = this._formatOfflineDuration(this._lastOfflineTime, Date.now());

    const referrer = this._authService.referrerAsJson;
    const site_id = this._conciergeService.siteId;
    const device_id = this._conciergeService.deviceId;
    /*
      The offline duration will be slightly inaccurate as it will be the duration since the last successful ping but its good enough for our metrics
      Using dayjs humanize will not return seconds in the output but given we are polling every 30 seconds in line with the CONNECTION_MONITOR_INTERVAL, the output is useful enough for our metric purpose
    */
    this._analyticsService.track(new METRIC.DeviceNetworkOffline(site_id, offlineDuration, referrer, device_id));
    this._lastOfflineTime = null;
  }
}
